import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { FEATURE_FLAGS } from '../config'
import { usePermissions } from '../composables/usePermissions'
import {
  getRoutePermissions,
  UNAUTHORIZED_ACCESS_CONFIG,
  DEFAULT_PERMISSION_BEHAVIOR,
  type PermissionRequirement,
} from '../config/permissions'
import { ProtectedRoute } from '../config/permissions'

// Debug gating for verbose permission logs
const DEBUG_PERMS = import.meta.env.VITE_DEBUG_PERMISSIONS === 'true'
const debug = (...args: any[]) => {
  if (DEBUG_PERMS) {
    console.log(...args)
  }
}

const publicRoutes = ['/auth/login', '/auth/signup', '/auth/recover-password', '/auth/check-email']

export const authGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  // If the route is public, skip
  if (publicRoutes.includes(to.path)) {
    // If the user is already authenticated, redirect to my-organization
    if (authStore.isAuthenticated) {
      return next({ name: 'my-organization' })
    }
    return next()
  }

  // Check authentication
  if (!authStore.isAuthenticated) {
    debug('User not authenticated, redirecting to login')
    return next({
      name: 'login',
      query: { redirect: to.fullPath },
      replace: true,
    })
  }

  return next()
}

/**
 * Guard для проверки доступности фич
 * Returns true if navigation should continue, false or redirect object if it should be blocked
 */
export function featureFlagGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): void {
  try {
    // Проверка фичи ролей и разрешений
    if (
      (to.name === 'roles-and-permissions' || to.name === 'admin-roles-and-permissions') &&
      !FEATURE_FLAGS.PERMISSION_GROUPS
    ) {
      // Если фича отключена, перенаправляем на главную страницу организации или админ панель
      if (to.name === 'admin-roles-and-permissions') {
        next({ name: 'admin-dashboard' })
        return
      } else {
        next({ name: 'my-organization' })
        return
      }
    }

    // Если фича включена или это не страница групп разрешений, не вызываем next здесь.
    // Передача управления следующему guard осуществляется в router/index.ts
    return
  } catch (error) {
    console.error('[Feature Flag Guard] Error:', error)
    // В случае ошибки, продолжаем навигацию на следующий guard, не вызывая next здесь
    return
  }
}

/**
 * Enhanced Permission Guard
 *
 * This guard checks route access permissions using a centralized configuration.
 * It properly handles both parent and child route permissions to ensure sub-pages
 * respect access controls.
 *
 * Permission Check Strategy:
 * 1. Check if route requires authentication
 * 2. Check for client user restrictions
 * 3. Check centralized permission config (preferred)
 * 4. Fallback to legacy meta-based permissions
 * 5. Apply deny-by-default for unconfigured routes
 *
 * Child Route Handling:
 * - Child routes are checked independently
 * - Both parent and child permissions must be satisfied
 * - More restrictive child permissions override parent permissions
 */
export async function permissionGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  try {
    debug(`[Permission Guard] ========================================`)
    debug(`[Permission Guard] Checking route: ${String(to.name)} (${to.fullPath})`)

    const authStore = useAuthStore()

    // Step 1: Check if route requires authentication
    const requiresAuth = to.matched.some((r) => r.meta?.requiresAuth)
    if (!requiresAuth) {
      debug(`[Permission Guard] Route does not require auth, allowing`)
      return next()
    }

    if (!authStore.isAuthenticated) {
      debug(`[Permission Guard] User not authenticated, redirecting to login`)
      return next({ name: 'login', query: { redirect: to.fullPath } })
    }

    // Step 2: Block pages for client users if specified
    const disallowClients = to.matched.some((r) => r.meta?.disallowClients === true)
    if (disallowClients && authStore.user?.is_client) {
      console.log(`[Permission Guard] Client users not allowed, redirecting`)
      return handleUnauthorizedAccess(to, authStore, next)
    }

    // Extract projectId from route params for project-specific permission checks
    const projectId = extractProjectId(to)
    debug(`[Permission Guard] Project ID from route:`, projectId)

    // Step 3: Check centralized permission configuration (PREFERRED METHOD)
    const routeName = String(to.name)
    const centralizedPermissions = getRoutePermissions(routeName)

    if (centralizedPermissions) {
      debug(`[Permission Guard] Using centralized permissions for route: ${routeName}`)
      debug(`[Permission Guard] Permission config:`, centralizedPermissions)

      const hasAccess = await checkCentralizedPermissions(centralizedPermissions, projectId, authStore)

      if (!hasAccess) {
        debug(`[Permission Guard] Access denied by centralized config`)
        logUnauthorizedAccess(to, authStore, 'centralized_config')
        return handleUnauthorizedAccess(to, authStore, next)
      }

      debug(`[Permission Guard] Access granted by centralized config`)
      return next()
    }

    // Step 4: Fallback to legacy meta-based permissions (for backward compatibility)
    debug(`[Permission Guard] No centralized config, checking legacy meta permissions`)

    // Check the CURRENT route (child) permissions, not all matched routes
    // This ensures child routes are checked independently
    const currentRoute = to.matched[to.matched.length - 1]
    const legacyPermissions = extractLegacyPermissions(currentRoute)

    debug(`[Permission Guard] Legacy permissions for current route:`, legacyPermissions)

    if (legacyPermissions.hasExplicit) {
      const hasAccess = await checkLegacyPermissions(legacyPermissions, projectId, authStore)

      if (!hasAccess) {
        debug(`[Permission Guard] Access denied by legacy meta permissions`)
        logUnauthorizedAccess(to, authStore, 'legacy_meta')
        return handleUnauthorizedAccess(to, authStore, next)
      }

      debug(`[Permission Guard] Access granted by legacy meta permissions`)
      return next()
    }

    // Step 5: Deny-by-default for unconfigured routes
    if (DEFAULT_PERMISSION_BEHAVIOR === 'DENY_BY_DEFAULT') {
      debug(`[Permission Guard] No explicit permissions configured, applying deny-by-default`)
      logUnauthorizedAccess(to, authStore, 'no_config_deny_by_default')

      // Log this as it indicates a configuration gap
      console.warn(`[Permission Guard] ⚠️ Route "${routeName}" requires auth but has no permission config`)

      return handleUnauthorizedAccess(to, authStore, next)
    }

    // If we somehow reach here, allow access (should not happen with deny-by-default)
    debug(`[Permission Guard] Fallthrough - allowing access`)
    return next()
  } catch (error) {
    console.error('[Permission Guard] Error during permission check:', error)
    // On error, fail closed (deny access) for security
    const authStore = useAuthStore()
    return handleUnauthorizedAccess(to, authStore, next)
  }
}

/**
 * Extract projectId from route parameters
 */
function extractProjectId(to: RouteLocationNormalized): number | undefined {
  if (to.params.projectId) {
    return parseInt(to.params.projectId as string)
  }
  if (to.params.id && to.path.includes('/projects/')) {
    return parseInt(to.params.id as string)
  }
  return undefined
}

/**
 * Check permissions using centralized configuration
 */
async function checkCentralizedPermissions(
  permissions: PermissionRequirement,
  projectId: number | undefined,
  authStore: any,
): Promise<boolean> {
  const { hasAnyPermission, hasAllPermissions } = usePermissions()

  // Superusers and owners bypass permission checks
  if (authStore.user?.is_superuser || authStore.user?.is_owner) {
    console.log(`[Permission Guard] User is superuser/owner, granting access`)
    return true
  }

  const roleNames = permissions.roles.map((role) => role.toString())

  if (permissions.type === 'ANY') {
    return await hasAnyPermission(roleNames, projectId)
  } else {
    return await hasAllPermissions(roleNames, projectId)
  }
}

/**
 * Extract legacy permission requirements from route meta
 */
function extractLegacyPermissions(route: any): {
  requiredAll: string[]
  requiredAny: string[]
  hasExplicit: boolean
} {
  const requiredAll =
    (route.meta?.requiredPermissions as string[] | undefined) ||
    (route.meta?.requiredAllPermissions as string[] | undefined) ||
    []
  const requiredAny = (route.meta?.requiredAnyPermissions as string[] | undefined) || []

  return {
    requiredAll,
    requiredAny,
    hasExplicit: requiredAll.length > 0 || requiredAny.length > 0,
  }
}

/**
 * Check permissions using legacy meta-based configuration
 */
async function checkLegacyPermissions(
  permissions: { requiredAll: string[]; requiredAny: string[] },
  projectId: number | undefined,
  authStore: any,
): Promise<boolean> {
  const { hasAnyPermission, hasAllPermissions } = usePermissions()

  // Superusers and owners bypass permission checks
  if (authStore.user?.is_superuser || authStore.user?.is_owner) {
    return true
  }

  // Check ALL permissions (AND logic)
  if (permissions.requiredAll.length > 0) {
    const hasAll = await hasAllPermissions(permissions.requiredAll, projectId)
    if (!hasAll) {
      console.log(`[Permission Guard] Missing required ALL permissions:`, permissions.requiredAll)
      return false
    }
  }

  // Check ANY permissions (OR logic)
  if (permissions.requiredAny.length > 0) {
    const hasAny = await hasAnyPermission(permissions.requiredAny, projectId)
    if (!hasAny) {
      console.log(`[Permission Guard] Missing required ANY permissions:`, permissions.requiredAny)
      return false
    }
  }

  return true
}

/**
 * Handle unauthorized access by redirecting to appropriate page
 */
async function handleUnauthorizedAccess(to: RouteLocationNormalized, authStore: any, next: NavigationGuardNext) {
  const redirectRoute = UNAUTHORIZED_ACCESS_CONFIG.redirectRoute

  // Avoid loops: if we're already on the redirect target, just proceed (or replace same)
  if (String(to.name) === redirectRoute) {
    return next()
  }

  // If not authenticated, send to login with redirect back
  if (!authStore.isAuthenticated) {
    debug(`[Permission Guard] Not authenticated, redirecting to login`)
    return next({ name: 'login', query: { redirect: to.fullPath }, replace: true })
  }

  // Try to find first accessible route for authenticated user using centralized permissions directly
  try {
    const orgId = authStore.organizationId
    const projectId = extractProjectId(to)
    const { hasAnyPermission, hasAllPermissions } = usePermissions()

    // Prefer Preferences for newly registered org members without roles
    const candidates: Array<{ name: string; params?: Record<string, any> }> = [
      { name: ProtectedRoute.PREFERENCES, params: orgId ? { orgId } : undefined },
      { name: ProtectedRoute.FAQ, params: orgId ? { orgId } : undefined },
      { name: ProtectedRoute.DASHBOARD, params: orgId ? { orgId } : undefined },
      { name: ProtectedRoute.PROJECTS, params: orgId ? { orgId } : undefined },
    ]

    for (const c of candidates) {
      const perm = getRoutePermissions(c.name)
      if (!perm) {
        debug(`[Permission Guard] Candidate ${c.name} has no centralized config, skipping`)
        continue
      }
      const roles = perm.roles.map((r) => r.toString())
      debug(
        `[Permission Guard] Evaluating candidate ${c.name} with roles`,
        roles,
        'type',
        perm.type,
        'projectId',
        projectId,
      )
      const ok =
        perm.type === 'ANY' ? await hasAnyPermission(roles, projectId) : await hasAllPermissions(roles, projectId)
      debug(`[Permission Guard] Candidate ${c.name} allowed =`, ok)
      if (ok) {
        debug(`[Permission Guard] Redirecting to first allowed route: ${c.name}`)
        return next({ name: c.name, params: c.params, replace: true })
      }
    }
  } catch (_) {
    console.warn('[Permission Guard] Failed to compute fallback route, using default')
  }

  // Fallback to safe public route
  debug(`[Permission Guard] Redirecting to ${redirectRoute}`)
  return next({ name: redirectRoute, replace: true })
}

/**
 * Log unauthorized access attempts for monitoring
 */
function logUnauthorizedAccess(to: RouteLocationNormalized, authStore: any, reason: string) {
  if (!UNAUTHORIZED_ACCESS_CONFIG.logAttempts) return

  console.warn(`[Permission Guard] 🚫 Unauthorized access attempt:`, {
    route: String(to.name),
    path: to.fullPath,
    user: authStore.user?.email || authStore.user?.id,
    reason,
    timestamp: new Date().toISOString(),
  })
}
